import { useState } from 'react';
import { resetAccount, setExitDefaults, setLimits, type ExitDefaults, type Limits, type LimitsChange, type Trading } from '../api/trading';
import { money, time } from '../format';

type Field = keyof Omit<Limits, 'version'>;
type Unit = 'money' | 'count' | 'seconds' | 'minutes' | 'cents';
/** Every guardrail, in the order a trader thinks about them. Optional ones can be switched off by leaving them blank. */
const FIELDS: { field: Field; label: string; unit: Unit; optional?: boolean; help: string }[] = [
  { field: 'per_order', label: 'Cost per order', unit: 'money', help: 'Most a single buy can cost, fees included' },
  { field: 'max_contracts', label: 'Contracts per order', unit: 'count', optional: true, help: 'Largest buy in contracts' },
  { field: 'per_hour', label: 'New risk per hour', unit: 'money', help: 'Buys placed in any rolling hour' },
  { field: 'max_open_risk', label: 'Total open risk', unit: 'money', optional: true, help: 'Lost if every position and resting buy loses' },
  { field: 'max_event_risk', label: 'Risk per expiry', unit: 'money', optional: true, help: 'The same, for one hourly or 15-minute close' },
  { field: 'daily_loss', label: 'Daily loss stop', unit: 'money', help: 'Realised losses before new buys stop for the day' },
  { field: 'entry_cutoff_seconds', label: 'No buys in the last', unit: 'seconds', help: 'Never less than the 60-second settlement average' },
  { field: 'min_buy_price', label: 'Lowest buy price', unit: 'cents', optional: true, help: 'Buys under this price are blocked; sells never are' },
  { field: 'max_buy_price', label: 'Highest buy price', unit: 'cents', optional: true, help: 'Buys over this price are blocked; sells never are' },
  { field: 'max_spread', label: 'Widest spread to take', unit: 'cents', optional: true, help: 'Buying at the ask is blocked when the spread is wider' },
  { field: 'loss_streak', label: 'Cooldown after', unit: 'count', optional: true, help: 'Losing settlements in a row' },
  { field: 'cooldown_minutes', label: 'Cooldown length', unit: 'minutes', help: 'No new buys for this long' },
];
const SUFFIX: Record<Unit, string> = { money: '', count: '', seconds: 's', minutes: 'min', cents: '¢' };

/** Stored values are dollars and whole numbers; the spread is shown in cents. */
const toInput = (f: (typeof FIELDS)[number], v: string | number | null | undefined) =>
  v == null ? '' : f.unit === 'cents' ? String(Math.round(Number(v) * 100)) : String(Number(v));
const fromInput = (f: (typeof FIELDS)[number], text: string): string | number | null =>
  text.trim() === '' ? null : f.unit === 'cents' ? (Number(text) / 100).toFixed(2)
    : f.unit === 'money' ? Number(text).toFixed(2).replace(/\.00$/, '')
    : f.field === 'max_contracts' ? String(Number(text)) : Number(text);
const shown = (f: (typeof FIELDS)[number], v: string | number | null | undefined) =>
  v == null ? 'Off' : f.unit === 'money' ? money(String(v), 0) : `${toInput(f, v)}${SUFFIX[f.unit] ? ` ${SUFFIX[f.unit]}` : ''}`;

/**
 * Your risk policy (plan 010). On the paper account every change applies at once (plan 066); elsewhere looser values
 * wait for the next day reset. The order service records every change as a version.
 */
export function LimitsPanel({ trading, onClose, notify }: { trading: Trading; onClose: () => void; notify: (text: string, tone?: 'up' | 'down' | 'info') => void }) {
  const a = trading.snapshot?.account;
  const [draft, setDraft] = useState<Record<string, string>>(() => Object.fromEntries(FIELDS.map(f => [f.field, toInput(f, a?.limits[f.field])])));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  if (!a) return null;
  const changes: LimitsChange = {};
  for (const f of FIELDS) {
    if (draft[f.field] === toInput(f, a.limits[f.field])) continue;
    if (draft[f.field].trim() === '' && !f.optional) continue;
    (changes as Record<string, unknown>)[f.field] = fromInput(f, draft[f.field]);
  }
  const save = async () => {
    setBusy(true); setError('');
    try {
      const r = await setLimits(changes);
      const later = r.policies.find(p => Date.parse(p.effective_from) > Date.now() + 1000);
      notify(later ? `Tighter limits apply now · Looser limits apply from ${time(later.effective_from)}` : 'Limits apply now', 'info');
      trading.refresh(); onClose();
    } catch (e) { setError(e instanceof Error ? e.message : 'The change wasn’t recorded'); }
    finally { setBusy(false); }
  };
  const pending = a.pending_limits;
  return <>
    <div className="scrim modal-scrim" onClick={onClose}/>
    <div className="confirm limits-panel" role="dialog" aria-modal="true" aria-label="Your limits" data-testid="limits-panel">
      <div className="confirm-body">
        <h3>Your limits <span className="faint">· policy v{a.limits.version}</span></h3>
        <p className="faint">Open risk {money(a.open_risk)} · today {Number(a.realized_today) >= 0 ? '+' : '−'}{money(Math.abs(Number(a.realized_today)))}
          {a.cooldown_until ? ` · cooling down until ${time(a.cooldown_until)}` : a.losing_streak ? ` · ${a.losing_streak} losing settlement${a.losing_streak === 1 ? '' : 's'} in a row` : ''}</p>
        <table className="limits">
          <thead><tr><th>Guardrail</th><th>Now</th><th>Change to</th></tr></thead>
          <tbody>{FIELDS.map(f => {
            const waiting = pending && pending[f.field] !== a.limits[f.field] ? pending[f.field] : undefined;
            return <tr key={f.field}>
              <th scope="row"><label htmlFor={`limit-${f.field}`}>{f.label}</label><small>{f.help}</small></th>
              <td>{shown(f, a.limits[f.field])}{waiting !== undefined && <small className="pending">→ {shown(f, waiting)} at {time(a.pending_from!)}</small>}</td>
              <td><span className="limit-input">{f.unit === 'money' && '$'}<input id={`limit-${f.field}`} aria-label={f.label} inputMode="decimal" value={draft[f.field]} placeholder={f.optional ? 'Off' : ''}
                onChange={e => setDraft(d => ({ ...d, [f.field]: e.target.value.trim() }))}/>{SUFFIX[f.unit]}</span></td>
            </tr>;
          })}</tbody>
        </table>
        <ExitDefaultsEditor defaults={a.exit_defaults ?? []} trading={trading} notify={notify}/>
        {a.environment === 'paper' && <PaperReset trading={trading} notify={notify} onDone={onClose}/>}
        <p className="faint">{a.environment === 'paper' ? 'Changes apply at once; each is recorded as a new policy version.'
          : <>Tighter values apply at once. Looser ones, and switching a guardrail off, apply at the next reset ({time(a.day_resets_at ?? "")}). Saving today’s values cancels a pending loosening.</>}</p>
        {error && <p className="note blocked" role="alert">{error}</p>}
      </div>
      <div className="confirm-actions">
        <button className="tool" onClick={onClose} autoFocus>Close</button>
        <button className="go buy" disabled={busy || (!Object.keys(changes).length && !pending)} onClick={save} data-testid="save-limits">
          {busy ? 'Saving…' : Object.keys(changes).length ? 'Save changes' : 'Keep today’s limits'}</button>
      </div>
    </div>
  </>;
}

const HORIZON_LABEL: Record<string, string> = { hourly: 'Hourly', '15m': '15-minute' };

/**
 * What every new buy's exits are (plan 066): the coin stop, which sells on a 15-second close past the coin's last
 * 5-candle swing against the trade, plus an optional take-profit and a dead-contract cut, per horizon. Applies at once.
 */
function ExitDefaultsEditor({ defaults, trading, notify }: { defaults: ExitDefaults[]; trading: Trading; notify: (text: string, tone?: 'up' | 'down' | 'info') => void }) {
  // Offsets show as hundredths either way: cents of the contract price, or percent of the buy's limit (plan 015).
  const hundredths = (v: string | null | undefined) => v == null ? '' : String(Math.round(Number(v) * 10000) / 100);
  const [draft, setDraft] = useState(() => Object.fromEntries(defaults.map(d => [d.horizon, {
    enabled: d.enabled, unit: d.unit ?? 'cents', tp: hundredths(d.take_profit_offset), slip: hundredths(d.stop_slippage), cut: hundredths(d.cut_below),
    cutLeft: d.cut_min_left == null ? '' : String(d.cut_min_left / 60),
  }])));
  const [error, setError] = useState('');
  const save = async () => {
    setError('');
    try {
      for (const d of defaults) {
        const x = draft[d.horizon];
        const offset = (v: string) => v === '' ? null : String(Number((Number(v) / 100).toFixed(x.unit === 'percent' ? 4 : 2)));
        const cutting = x.cut !== '' || x.cutLeft !== '';
        // The coin stop is the only stop (plan 066).
        await setExitDefaults(d.horizon, {
          enabled: x.enabled, unit: x.unit, take_profit_offset: offset(x.tp), stop_slippage: (Number(x.slip) / 100).toFixed(2), structure: true,
          cut_below: cutting && x.cut !== '' ? (Number(x.cut) / 100).toFixed(2) : null,
          cut_min_left: cutting && x.cutLeft !== '' ? Math.round(Number(x.cutLeft) * 60) : null,
        });
      }
      notify('Exit defaults saved', 'info'); trading.refresh();
    } catch (e) { setError(e instanceof Error ? e.message : 'The defaults weren’t saved'); }
  };
  if (!defaults.length) return null;
  const set = (h: string, k: string, v: string | boolean) => setDraft(d => ({ ...d, [h]: { ...d[h], [k]: v } }));
  return <section className="exit-defaults" data-testid="exit-defaults">
    <h4>Exits on every buy</h4>
    {defaults.map(d => {
      const x = draft[d.horizon], name = HORIZON_LABEL[d.horizon] ?? d.horizon, unit = x.unit === 'percent' ? '%' : '¢';
      const field = (key: string, label: string, aria: string, sign: string, suffix: string, placeholder = '') =>
        <label className="exit-default"><span>{label}</span><span className="limit-input">{sign}<input aria-label={`${name} ${aria}`} inputMode="decimal" value={(x as Record<string, string | boolean>)[key] as string}
          placeholder={placeholder} onChange={e => set(d.horizon, key, e.target.value.trim())}/>{suffix}</span></label>;
      return <div className="exit-horizon" key={d.horizon} data-testid={`exit-defaults-${d.horizon}`}>
        <div className="exit-horizon-head"><b>{name}</b>
          <label><input type="checkbox" aria-label={`Exits on ${name} buys`} checked={x.enabled} onChange={e => set(d.horizon, 'enabled', e.target.checked)}/> On</label>
          <select aria-label={`${name} exit unit`} value={x.unit} onChange={e => set(d.horizon, 'unit', e.target.value)}>
            <option value="cents">¢</option><option value="percent">%</option></select></div>
        <div className="exit-default-grid">
          {field('tp', 'Take-profit', 'take-profit offset', '+', unit, 'None')}
          {field('cut', 'Cut under', 'cut price', '', '¢', 'Off')}
          {field('cutLeft', 'With at least', 'cut minutes left', '', 'min', 'Off')}
          {field('slip', 'Sell down to', 'stop slippage', '', '¢ under the bid')}
        </div>
      </div>;
    })}
    <p className="faint">Every buy’s stop is the coin stop: it sells into the bid when a 15-second candle closes beyond the coin’s last 5-candle swing against the trade (below the last swing low on an up trade, above the last swing high on a down one), and it moves only with the trade as new swings form. Range buckets have no up or down, so they have no automatic stop. The take-profit is from each buy’s limit, in cents or percent; without one a winner rides to settlement. The cut sells a side bidding under its price while that much time is left.</p>
    {error && <p className="note blocked" role="alert">{error}</p>}
    <button className="tool" onClick={save} data-testid="save-exit-defaults">Save exit defaults</button>
  </section>;
}

/**
 * Plan 035: start a fresh paper run. Cash, today's P&L, the hourly spend and the losing streak count from the reset;
 * resting orders are cancelled and history is kept. The order service refuses while contracts are held.
 */
function PaperReset({ trading, notify, onDone }: { trading: Trading; notify: (text: string, tone?: 'up' | 'down' | 'info') => void; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('1000');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const valid = /^\d{1,6}(\.\d{1,2})?$/.test(amount) && Number(amount) >= 1 && Number(amount) <= 100000;
  const reset = async () => {
    setBusy(true); setError('');
    try {
      const r = await resetAccount({ starting_cash: amount });
      notify(`Paper account reset to ${money(r.starting_cash)}${r.cancelled ? ` · ${r.cancelled} resting order${r.cancelled === 1 ? '' : 's'} cancelled` : ''}`, 'info');
      trading.refresh(); onDone();
    } catch (e) { setError(e instanceof Error ? e.message : 'The account wasn’t reset'); }
    finally { setBusy(false); }
  };
  return <section className="paper-reset" data-testid="paper-reset">
    <h4>Practice run</h4>
    {!open
      ? <button className="tool" onClick={() => setOpen(true)} data-testid="reset-account">Reset paper account…</button>
      : <>
        <p>Cash goes back to the amount below. Today’s P&L, the hourly spend and the losing streak start fresh, and resting orders are cancelled. Your fills and settlements stay in the history.</p>
        <label className="reset-amount">Starting cash <span className="limit-input">$<input aria-label="Starting cash" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value.trim())}/></span></label>
        <div className="reset-actions">
          <button className="tool" onClick={() => { setOpen(false); setError(''); }}>Cancel</button>
          <button className="go buy" disabled={busy || !valid} onClick={reset} data-testid="confirm-reset">{busy ? 'Resetting…' : valid ? `Reset to ${money(amount)}` : 'Enter $1 to $100,000'}</button>
        </div>
      </>}
    {error && <p className="note blocked" role="alert">{error}</p>}
  </section>;
}
